import React from 'react';
import { 
  ShieldCheck, Lock, User, Key, ArrowRight, CheckCircle2, 
  Building2, Users, GraduationCap, LayoutDashboard, 
  Sparkles, ArrowLeft, Layers, BookOpen, Award, ArrowUpRight,
  ShieldAlert, HelpCircle
} from 'lucide-react';
import { INSTITUTION_INFO } from '../../data/portalData';

export const CentralLoginHub = ({ setActiveView }) => {
  const portalCards = [
    {
      id: 'manager',
      view: 'lms-login-manager',
      title: 'Global Content Manager',
      category: 'Courseware & Curriculum CMS',
      tierBadge: 'Tier 1 · Global CMS Admin',
      tierColor: 'bg-cyan-500/10 text-cyan-300 border-cyan-400/40',
      icon: Layers,
      iconBg: 'from-cyan-900 via-slate-900 to-[#0B2644] border-cyan-400/50 text-cyan-300',
      description: 'Centralized curriculum architecture, multimedia lecture CDN repository, and courseware governance desk.',
      features: [
        'Curriculum repository for all 6 Executive Programmes',
        'Video lecture uploads, slide decks & case library',
        'Cohort release schedules & syllabus version control'
      ],
      actionLabel: 'Enter CMS Login Desk',
      accentColor: 'hover:border-cyan-400/80 group-hover:text-cyan-300'
    },
    {
      id: 'director',
      view: 'lms-login-director',
      title: 'Executive Directorate Console',
      category: 'Institutional Leadership & Governance',
      tierBadge: 'Tier 1 · Senate Oversight',
      tierColor: 'bg-amber-500/10 text-amber-300 border-[#B87A28]/50',
      icon: ShieldCheck,
      iconBg: 'from-[#0B2644] via-[#06182B] to-[#030F1C] border-[#B87A28]/60 text-amber-300',
      description: 'Executive authority console for admission clearance, financial settlement reconciliation, and school governance.',
      features: [
        'Doctoral & Masters applicant screening oversight',
        'Bursary settlement & Paystack revenue audits',
        'Senate academic clearance & graduation roll'
      ],
      actionLabel: 'Enter Directorate Login Desk',
      accentColor: 'hover:border-amber-400/80 group-hover:text-amber-300'
    },
    {
      id: 'faculty',
      view: 'lms-login-faculty',
      title: 'Executive Faculty Portal',
      category: 'Academic Delivery & Seminar Desk',
      tierBadge: 'Tier 2 · Course Chair Desk',
      tierColor: 'bg-amber-500/10 text-amber-300 border-amber-500/40',
      icon: BookOpen,
      iconBg: 'from-[#7A470E] via-[#5C3306] to-[#0B2644] border-amber-400/50 text-amber-200',
      description: 'Dedicated academic workbench for professors, course chairs, and executive syndicate directors.',
      features: [
        'Live interactive executive seminars & case webinars',
        'Coursework assessment rubrics & grading desk',
        'Enrolled candidate syndicate progress analytics'
      ],
      actionLabel: 'Enter Faculty Login Desk',
      accentColor: 'hover:border-amber-400/80 group-hover:text-amber-300'
    },
    {
      id: 'student',
      view: 'lms-login-student',
      title: 'Student LMS & Scholar Registration',
      category: 'Executive Scholar Workspace',
      tierBadge: 'Tier 3 · Enrolled Scholars & Fresh Admitted',
      tierColor: 'bg-emerald-500/10 text-emerald-300 border-emerald-400/40',
      icon: GraduationCap,
      iconBg: 'from-emerald-900 via-slate-900 to-[#0B2644] border-emerald-400/50 text-emerald-300',
      description: 'Personalized digital campus for DBA, PhD, MBA, and PGD scholars, plus portal activation for newly admitted candidates.',
      features: [
        'Dedicated DBA, PhD, MBA & PGD study tracks',
        'Silicon AI 24/7 executive tutor & case analysis',
        'Portal Registration & Activation for new intakes'
      ],
      actionLabel: 'Enter Student Login & Registration',
      accentColor: 'hover:border-emerald-400/80 group-hover:text-emerald-300'
    }
  ];

  return (
    <div className="min-h-screen bg-[#030F1C] text-slate-100 flex flex-col justify-between relative overflow-hidden">
      {/* Subtle luxury ambient glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#7A470E]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-[#0B2644]/40 rounded-full blur-3xl pointer-events-none" />

      {/* Top Bar with Return Navigation */}
      <header className="border-b border-slate-800/80 bg-[#06182B]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => setActiveView('home')}
            className="flex items-center space-x-2 text-xs font-bold text-slate-300 hover:text-white bg-[#0B2644] hover:bg-[#7A470E] px-3.5 py-2 rounded-xl border border-slate-700/80 transition-all font-sc cursor-pointer shadow-xs group"
          >
            <ArrowLeft className="w-4 h-4 text-amber-300 group-hover:-translate-x-0.5 transition-transform" />
            <span>Return to Public Website</span>
          </button>

          <div className="flex items-center space-x-3">
            <span className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-[11px] font-mono font-semibold bg-emerald-950/80 text-emerald-300 border border-emerald-600/40">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse mr-2" />
              Central Identity Gateway · RBAC Active
            </span>
            <div className="flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-mono bg-black/40 text-amber-300 border border-[#7A470E]/40">
              <Lock className="w-3.5 h-3.5" />
              <span>TLS 1.3 256-bit Encrypted</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 w-full flex-1 flex flex-col justify-center">
        
        {/* Institutional Identity Heading */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-white rounded-2xl shadow-2xl border-2 border-[#7A470E]">
            <img 
              src="/Logo.png" 
              alt="AKSU Crest" 
              className="h-16 w-auto object-contain logo-glow"
            />
          </div>

          <div>
            <div className="inline-flex items-center space-x-2 px-3.5 py-1 rounded-full bg-[#0B2644] border border-[#7A470E]/50 text-amber-300 text-xs font-bold tracking-wider uppercase font-sc mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Akwa Ibom State University Executive Business School</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black font-sc text-white tracking-tight leading-tight">
              Central Portal Authentication Hub
            </h1>
            <p className="text-sm sm:text-base text-slate-300 mt-3 font-light leading-relaxed">
              Select your assigned institutional desk below. Each workspace provides a dedicated, highly secure authentication and registration gate tailored to your role.
            </p>
          </div>
        </div>

        {/* 4 CARDS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {portalCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.id}
                onClick={() => {
                  setActiveView(card.view);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="group relative bg-gradient-to-b from-[#082038] via-[#06182B] to-[#04101D] rounded-3xl p-6 border-2 border-slate-800 hover:border-[#B87A28] transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1.5 cursor-pointer flex flex-col justify-between"
              >
                {/* Card Top: Tier Badge & Icon */}
                <div>
                  <div className="flex items-center justify-between gap-2 mb-5">
                    <span className={`text-[10px] font-mono font-bold px-2.5 py-1 rounded-full border ${card.tierColor}`}>
                      {card.tierBadge}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 group-hover:text-amber-300 group-hover:border-amber-400/50 group-hover:bg-amber-400/10 transition-all">
                      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>

                  <div className="flex items-center space-x-3.5 mb-4">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${card.iconBg} border flex items-center justify-center shadow-md group-hover:scale-105 transition-transform shrink-0`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="text-[11px] font-mono text-amber-300 uppercase tracking-wider font-semibold">
                        {card.category}
                      </div>
                      <h2 className="text-base sm:text-lg font-black font-sc text-white leading-tight mt-0.5 group-hover:text-amber-200 transition-colors">
                        {card.title}
                      </h2>
                    </div>
                  </div>

                  <p className="text-xs text-slate-300 font-light leading-relaxed mb-5">
                    {card.description}
                  </p>

                  {/* Scoped privileges checklist */}
                  <div className="space-y-2 pt-3 border-t border-slate-800/80 mb-6">
                    {card.features.map((feat, i) => (
                      <div key={i} className="flex items-start space-x-2 text-[11px] text-slate-300">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                        <span className="leading-snug">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveView(card.view);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-[#0B2644] to-[#06182B] group-hover:from-[#7A470E] group-hover:to-[#5C3306] text-white border border-[#B87A28]/40 group-hover:border-amber-400 font-sc font-bold text-xs flex items-center justify-center space-x-2 transition-all shadow-md group-hover:shadow-lg cursor-pointer"
                >
                  <span>{card.actionLabel}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-amber-300 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Lower Candidate & Help Support Banner */}
        <div className="mt-12 bg-gradient-to-r from-[#082038] via-[#0B2E54] to-[#082038] rounded-3xl p-6 border border-[#B87A28]/40 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-400/10 border border-amber-300/40 flex items-center justify-center text-amber-300 shrink-0">
              <Award className="w-6 h-6" />
            </div>
            <div>
              <div className="text-sm font-bold font-sc text-white">
                Prospective Candidate for October 2026 Commencement?
              </div>
              <p className="text-xs text-slate-300 mt-0.5">
                New applicants must first complete the Online Admission Application and receive their Official Offer Letter before activating their Student LMS Portal.
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 shrink-0">
            <button
              onClick={() => setActiveView('apply')}
              className="px-4 py-2.5 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white text-xs font-bold font-sc transition-colors cursor-pointer shadow-sm"
            >
              Apply for Admission
            </button>
            <button
              onClick={() => setActiveView('screening')}
              className="px-4 py-2.5 rounded-xl bg-[#030F1C] hover:bg-[#0B2644] text-amber-300 border border-amber-400/30 text-xs font-bold font-sc transition-colors cursor-pointer shadow-sm"
            >
              Track Screening Status
            </button>
          </div>
        </div>

      </main>

      {/* Footer Strip */}
      <footer className="border-t border-slate-800/80 bg-[#020A14] py-5 px-4 text-center text-xs text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-mono text-[11px] text-slate-400">
            © 2026 {INSTITUTION_INFO.name} · All Rights Reserved
          </span>
          <div className="flex items-center space-x-4 text-[11px]">
            <span className="text-emerald-400 font-mono">● All Systems Operational</span>
            <span>·</span>
            <button onClick={() => setActiveView('privacy')} className="hover:text-amber-300 transition-colors cursor-pointer">
              Privacy Policy
            </button>
            <span>·</span>
            <button onClick={() => setActiveView('terms')} className="hover:text-amber-300 transition-colors cursor-pointer">
              Terms of Use
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CentralLoginHub;
